import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { OnboardingForm, ResearchProgress, CompanySummary } from '../components/strategy-coach/OnboardingForm';
import { ConfidenceBoost } from '../components/strategy-coach/ConfidenceBoost';
import { ModeSelection, AdvisoryRecommendation } from '../components/strategy-coach/ModeSelection';
import { MeetingRoom } from '../components/strategy-coach/MeetingRoom';
import { ExecutiveReport } from '../components/strategy-coach/ExecutiveReport';
import { InterviewView } from '../components/strategy-coach/InterviewView';
import { DiagnosisDashboard } from '../components/strategy-coach/DiagnosisDashboard';
import { SharpenFocus } from '../components/strategy-coach/SharpenFocus';
import { RecommendationGenerator, RecommendationView } from '../components/strategy-coach/ExecutiveRecommendation';

const CONFIDENCE_THRESHOLD = 75;

// Flow: onboarding → research → (confidence_boost if <75%) → summary → mode → (strategy | advisory | meeting | focus) → dashboard/report
export default function StrategyCoachPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [view, setView] = useState(sessionId ? 'loading' : 'init');
  const [activeSessionId, setActiveSessionId] = useState(sessionId || null);
  const [initialMessages, setInitialMessages] = useState([]);
  const [initialCoverage, setInitialCoverage] = useState({});
  const [diagnosis, setDiagnosis] = useState(null);
  const [starting, setStarting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [kbData, setKbData] = useState(null);
  const [meetingData, setMeetingData] = useState(null);
  const [recommendationData, setRecommendationData] = useState(null);
  const [companyName, setCompanyName] = useState('');

  // On mount: check if user already has a confirmed KB
  useEffect(() => {
    if (sessionId) return;
    (async () => {
      try {
        const res = await apiGet('/company/knowledge-base');
        if (res.ok) {
          const data = await res.json();
          if (data.exists && data.confirmed) {
            setKbData(data); setCompanyName(data.company_name || ''); setView('mode'); return;
          }
          if (data.exists && !data.confirmed) {
            setKbData(data); setCompanyName(data.company_name || '');
            // Check confidence — if below threshold, show boost screen
            if ((data.confidence_score || 0) < CONFIDENCE_THRESHOLD) {
              setView('confidence_boost'); return;
            }
            setView('summary'); return;
          }
        } else if (res.status === 401) {
          return;
        }
      } catch (err) {
        console.warn('[Coach] KB check failed:', err);
      }
      setView('onboarding');
    })();
  }, [sessionId]);

  // Load existing session from URL
  useEffect(() => {
    if (!sessionId) return;
    apiGet(`/strategy-coach/sessions/${sessionId}`).then(async r => {
      if (!r.ok) { setView('onboarding'); return; }
      const session = await r.json();
      if (session.status === 'completed' && session.diagnosis) {
        setDiagnosis(session.diagnosis); setActiveSessionId(sessionId); setView('dashboard');
      } else if (session.messages?.length > 0) {
        setActiveSessionId(sessionId);
        setInitialMessages(session.messages.map(m => ({ role: m.role, content: m.content, theme: m.theme })));
        setInitialCoverage(session.theme_coverage || {});
        setView('interview');
      } else { setView('onboarding'); }
    }).catch(() => setView('onboarding'));
  }, [sessionId]);

  const handleOnboard = async (form) => {
    try {
      const res = await apiPost('/company/onboard', form);
      if (res.ok) {
        const data = await res.json();
        setJobId(data.job_id); setCompanyName(form.company_name); setView('research');
      } else toast.error('Failed to start');
    } catch { toast.error('Connection error'); }
  };

  const handleResearchComplete = async () => {
    try {
      const res = await apiGet('/company/knowledge-base');
      if (res.ok) {
        const data = await res.json();
        if (data.exists) {
          setKbData(data);
          setCompanyName(data.company_name || '');
          // Confidence gate: if below threshold, show boost options
          if ((data.confidence_score || 0) < CONFIDENCE_THRESHOLD) {
            setView('confidence_boost'); return;
          }
          setView('summary'); return;
        }
      }
    } catch { /* network error */ }
    toast.error('Failed to load knowledge base');
  };

  // After confidence boost completes (confidence >= threshold)
  const handleConfidenceMet = (newKb) => {
    setKbData(newKb);
    setCompanyName(newKb.company_name || companyName);
    setView('summary');
  };

  // User skips confidence boost
  const handleConfidenceSkip = async () => {
    try {
      const res = await apiGet('/company/knowledge-base');
      if (res.ok) {
        const data = await res.json();
        setKbData(data);
      }
    } catch { /* use existing */ }
    setView('summary');
  };

  const handleStrategyMode = async () => {
    setStarting(true);
    try {
      const jt = kbData?.journey_type || 'find_opportunities';
      const challenge = kbData?.challenge || '';
      const res = await apiPost('/strategy-coach/sessions', { journey_type: jt, custom_question: challenge });
      if (res.ok) {
        const data = await res.json();
        setActiveSessionId(data.session_id);
        setInitialMessages([{ role: 'assistant', content: data.question, theme: data.theme }]);
        setInitialCoverage(data.theme_coverage || {});
        navigate(`/strategy-coach/${data.session_id}`, { replace: true });
        setView('interview');
      } else {
        let detail = 'Unknown error';
        try { const err = await res.json(); detail = err.detail || detail; } catch { /* not json */ }
        if (res.status === 401) toast.error('Session expired. Please log in again.');
        else if (res.status === 500) toast.error('Could not start strategy session. Please try again.');
        else toast.error(detail);
      }
    } catch (err) {
      toast.error('Connection error. Please check your network.');
    }
    setStarting(false);
  };

  // Handle "Start New Session" — go back to onboarding
  const handleNewSession = () => {
    setKbData(null);
    setCompanyName('');
    setView('onboarding');
  };

  if (view === 'loading' || view === 'init') return (
    <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-odapto-orange" /></div>
  );
  if (starting) return (
    <div className="flex items-center justify-center h-full"><div className="text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-odapto-orange" /><p className="text-muted-foreground">Starting your strategic interview...</p></div></div>
  );

  switch (view) {
    case 'onboarding': return <OnboardingForm userName={user?.name} onSubmit={handleOnboard} initialData={kbData?.exists ? kbData : null} />;
    case 'research': return <ResearchProgress jobId={jobId} onComplete={handleResearchComplete} />;
    case 'confidence_boost': return <ConfidenceBoost kb={kbData} onComplete={handleConfidenceMet} onSkip={handleConfidenceSkip} />;
    case 'summary': return <CompanySummary kb={kbData} onConfirm={() => setView('mode')} onEdit={() => setView('onboarding')} onAddMore={() => setView('confidence_boost')} />;
    case 'mode': return <ModeSelection companyName={companyName} onSelectStrategy={handleStrategyMode} onSelectAdvisory={() => setView('advisory')} onSelectFocus={() => setView('focus')} onSelectRecommendation={() => setView('recommendation')} onNewSession={handleNewSession} />;
    case 'advisory': return <AdvisoryRecommendation onBack={() => setView('mode')} onBoardReady={() => setView('meeting')} />;
    case 'meeting': return <MeetingRoom onBack={() => setView('mode')} onReport={(m) => { setMeetingData(m); setView('report'); }} />;
    case 'report': return <ExecutiveReport report={meetingData?.executive_report} meeting={meetingData} onBack={() => setView('meeting')} />;
    case 'focus': return <SharpenFocus onBack={() => setView('mode')} onAdvisory={() => setView('advisory')} />;
    case 'recommendation': return <RecommendationGenerator onBack={() => setView('mode')} onComplete={(data) => { setRecommendationData(data); setView('recommendation_view'); }} />;
    case 'recommendation_view': return <RecommendationView data={recommendationData} onBack={() => setView('mode')} onNewRecommendation={() => { setRecommendationData(null); setView('recommendation'); }} />;
    case 'interview': return <InterviewView sessionId={activeSessionId} initialMessages={initialMessages} initialCoverage={initialCoverage} onComplete={d => { setDiagnosis(d); setView('dashboard'); }} />;
    case 'dashboard': return <DiagnosisDashboard diagnosis={diagnosis} sessionId={activeSessionId} />;
    default: return <OnboardingForm userName={user?.name} onSubmit={handleOnboard} initialData={kbData?.exists ? kbData : null} />;
  }
}
